import { useEffect, useState } from "react";
import api from "../../lib/api";
import { CreditCard, Eye, Ban, Trash2, Shield, Banknote, Pencil } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";

const CARD_TYPES = ["Visa", "Mastercard", "Verve", "American Express", "Discover", "UnionPay"];
const STATUSES = ["Active", "Inactive", "Blocked"];

const TYPE_COLORS = {
  Visa: "bg-blue-500/15 text-blue-500",
  Mastercard: "bg-orange-500/15 text-orange-500",
  Verve: "bg-green-500/15 text-green-500",
  "American Express": "bg-cyan-500/15 text-cyan-500",
  Discover: "bg-amber-500/15 text-amber-500",
  UnionPay: "bg-rose-500/15 text-rose-500",
};

const STATUS_COLORS = {
  Active: "bg-primary/15 text-primary",
  Inactive: "bg-muted text-muted-foreground",
  Blocked: "bg-destructive/15 text-destructive",
};

export function CardGrid({ cards, onView, onBlock, onDelete, onSpend, onEdit, showOwner }) {
  if (!cards.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border bg-card py-16 text-center text-sm text-muted-foreground">
        <CreditCard className="h-8 w-8 opacity-40" />
        No cards found.
      </div>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {cards.map((c) => (
        <div
          key={c.id}
          data-testid={`gcard-${c.id}`}
          className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-card to-muted/40 p-5"
        >
          <div className="flex items-start justify-between">
            <span className={`rounded px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${TYPE_COLORS[c.card_type] || "bg-secondary"}`}>
              {c.card_type}
            </span>
            <span className={`rounded px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_COLORS[c.status] || "bg-secondary"}`}>
              {c.status}
            </span>
          </div>
          <div className="mt-6 font-mono text-lg tracking-widest">{c.card_mask}</div>
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Holder</div>
              <div className="text-sm font-medium">{c.cardholder_name}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-muted-foreground">Expires</div>
              <div className="text-sm font-medium">{c.expiry_month}/{c.expiry_year}</div>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span>{c.card_category}</span>·<span>{c.currency}</span>
            {showOwner && <>·<span className="truncate">{c.owner_name}</span></>}
          </div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => onView(c)} data-testid={`view-gcard-${c.id}`}>
              <Eye className="mr-1 h-3.5 w-3.5" /> View
            </Button>
            {onSpend && (
              <Button size="sm" variant="outline" onClick={() => onSpend(c)} disabled={c.status !== "Active"} data-testid={`spend-gcard-${c.id}`} title={c.status !== "Active" ? "Card must be Active to spend" : "Spend from Card account"}>
                <Banknote className="h-3.5 w-3.5" />
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`edit-gcard-${c.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {onBlock && (
              <Button size="sm" variant="outline" onClick={() => onBlock(c)} data-testid={`block-gcard-${c.id}`}>
                <Ban className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="outline" onClick={() => onDelete(c)} data-testid={`delete-gcard-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CardDetailDialog({ card, onClose }) {
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gcard-detail">
        <DialogHeader>
          <DialogTitle>Card details</DialogTitle>
          <DialogDescription>CVV is encrypted and never shown. Card number is masked.</DialogDescription>
        </DialogHeader>
        {card && (
          <div className="space-y-3 text-sm">
            {[
              ["Card type", card.card_type],
              ["Category", card.card_category],
              ["BIN", card.bin || "—"],
              ["Number", card.card_mask],
              ["Expiry", `${card.expiry_month}/${card.expiry_year}`],
              ["CVV", "••• (encrypted)"],
              ["Holder", card.cardholder_name],
              ["Issuing bank", card.issuing_bank || "—"],
              ["Currency", card.currency],
              ["Daily limit", `${card.daily_limit}`],
              ["Monthly limit", `${card.monthly_limit}`],
              ["International", card.international ? "On" : "Off"],
              ["Online", card.online ? "On" : "Off"],
              ["ATM", card.atm ? "On" : "Off"],
              ["Status", card.status],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-border/60 pb-1.5">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium text-right">{v}</span>
              </div>
            ))}
            <div className="pt-1">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <Shield className="h-3.5 w-3.5" /> Billing address
              </div>
              <div className="text-sm">
                {[card.billing_address?.street, card.billing_address?.city, card.billing_address?.state,
                  card.billing_address?.postal_code, card.billing_address?.country].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MyCards() {
  const [cards, setCards] = useState([]);
  const [detail, setDetail] = useState(null);
  const [spendTarget, setSpendTarget] = useState(null);
  const [spendAmount, setSpendAmount] = useState("");
  const [editCard, setEditCard] = useState(null);
  const [ef, setEf] = useState(null);
  const [delCard, setDelCard] = useState(null);

  const load = () => api.get("/generated-cards").then((r) => setCards(r.data.cards)).catch(() => {});
  useEffect(() => { load(); }, []);

  const block = async (c) => {
    const r = await api.post(`/generated-cards/${c.id}/block`);
    toast.success(`Card ${r.data.status === "Blocked" ? "blocked" : "unblocked"}`);
    load();
  };
  const confirmDelete = async () => {
    await api.delete(`/generated-cards/${delCard.id}`);
    toast.success("Card deleted");
    setDelCard(null);
    load();
  };
  const startEdit = (c) => {
    setEditCard(c);
    setEf({
      card_type: c.card_type, card_category: c.card_category, bin: c.bin || "",
      card_number: "****", expiry_month: c.expiry_month, expiry_year: c.expiry_year, cvv: "",
      cardholder_name: c.cardholder_name, issuing_bank: c.issuing_bank || "", currency: c.currency,
      billing_address: { ...{ street: "", city: "", state: "", postal_code: "", country: "United states" }, ...(c.billing_address || {}) },
      daily_limit: c.daily_limit, monthly_limit: c.monthly_limit,
      international: c.international, online: c.online, atm: c.atm, status: c.status,
    });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/generated-cards/${editCard.id}`, { ...ef, daily_limit: Number(ef.daily_limit) || 0, monthly_limit: Number(ef.monthly_limit) || 0 });
      toast.success("Card updated");
      setEditCard(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const confirmSpend = async () => {
    const amt = parseFloat(spendAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const r = await api.post(`/generated-cards/${spendTarget.id}/spend`, { amount: amt, mode: "card" });
      toast.success(`Spent $${amt.toFixed(2)} · daily $${r.data.daily_spent} / monthly $${r.data.monthly_spent}`);
      setSpendTarget(null);
      setSpendAmount("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Issued Cards</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">My Cards</h1>
        <p className="mt-2 text-sm text-muted-foreground">Your generated cards — numbers masked, CVV encrypted. Spend draws from your Card account balance and respects each card's limits.</p>
      </div>
      <CardGrid cards={cards} onView={setDetail} onBlock={block} onDelete={setDelCard} onEdit={startEdit} onSpend={(c) => { setSpendTarget(c); setSpendAmount(""); }} />
      <CardDetailDialog card={detail} onClose={() => setDetail(null)} />

      <Dialog open={!!editCard} onOpenChange={(o) => !o && setEditCard(null)}>
        <DialogContent data-testid="edit-gcard-dialog">
          <DialogHeader>
            <DialogTitle>Edit card</DialogTitle>
            <DialogDescription>Update card details. Number and CVV stay encrypted unless you re-enter them.</DialogDescription>
          </DialogHeader>
          {ef && (
            <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto pr-1">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={ef.card_type} onValueChange={(v) => setEf({ ...ef, card_type: v })}>
                  <SelectTrigger data-testid="ef-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CARD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
                  <SelectTrigger data-testid="ef-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Holder</Label><Input value={ef.cardholder_name} onChange={(e) => setEf({ ...ef, cardholder_name: e.target.value })} data-testid="ef-holder" /></div>
              <div className="space-y-1.5"><Label>Issuing bank</Label><Input value={ef.issuing_bank} onChange={(e) => setEf({ ...ef, issuing_bank: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Exp month</Label><Input value={ef.expiry_month} onChange={(e) => setEf({ ...ef, expiry_month: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Exp year</Label><Input value={ef.expiry_year} onChange={(e) => setEf({ ...ef, expiry_year: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Daily limit</Label><Input type="number" value={ef.daily_limit} onChange={(e) => setEf({ ...ef, daily_limit: e.target.value })} data-testid="ef-daily" /></div>
              <div className="space-y-1.5"><Label>Monthly limit</Label><Input type="number" value={ef.monthly_limit} onChange={(e) => setEf({ ...ef, monthly_limit: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={ef.billing_address.city} onChange={(e) => setEf({ ...ef, billing_address: { ...ef.billing_address, city: e.target.value } })} /></div>
              <div className="space-y-1.5"><Label>Country</Label><Input value={ef.billing_address.country} onChange={(e) => setEf({ ...ef, billing_address: { ...ef.billing_address, country: e.target.value } })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditCard(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-gcard-edit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delCard} onOpenChange={(o) => !o && setDelCard(null)}>
        <AlertDialogContent data-testid="delete-gcard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this card?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the card {delCard?.card_mask}. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-gcard" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!spendTarget} onOpenChange={(o) => !o && setSpendTarget(null)}>
        <DialogContent data-testid="spend-dialog">
          <DialogHeader>
            <DialogTitle>Spend on card</DialogTitle>
            <DialogDescription>Draws from your Card account balance; blocked if it exceeds the card's daily or monthly limit.</DialogDescription>
          </DialogHeader>
          {spendTarget && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                <div className="font-mono">{spendTarget.card_mask}</div>
                <div className="text-xs text-muted-foreground">Daily limit ${spendTarget.daily_limit} · Monthly ${spendTarget.monthly_limit}</div>
              </div>
              <div className="space-y-1.5">
                <Label>Amount (USD)</Label>
                <Input type="number" step="0.01" value={spendAmount} onChange={(e) => setSpendAmount(e.target.value)} placeholder="25.00" data-testid="spend-amount" autoFocus />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSpendTarget(null)}>Cancel</Button>
            <Button onClick={confirmSpend} data-testid="confirm-spend">Record spend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
